<template>
  <b-overlay :show="isLoading" rounded="sm" :opacity="0.5">
    <b-row class="m-0">
      <b-col cols="12">
        <div
          class="mb-3 bg-white rounded border p-2 w-100 d-flex align-items-center justify-content-around"
        >
          <b-button
            :disabled="!prevCandidate"
            :variant="prevCandidate ? 'primary' : 'secondary'"
            @click="prevChange"
          >
            <mdicon name="chevron-right" size="24" />
            <span>{{ $lang.paginate.prev }}</span>
          </b-button>
          <AvatarInfo
            :avatarAddress="pathOr(null, ['person', 'img_thumb'], candidate)"
            :imageAddress="pathOr(null, ['person', 'img_original'], candidate)"
            :title="pathOr('*****', ['person', 'full_name'], candidate)"
            :description="
              `${$lang.candidateCode}: ` +
              pathOr('', ['person_center_execute_code'], candidate)
            "
            :genderId="+pathOr(1, ['person', 'gender', 'id'], candidate)"
            :personCode="pathOr(null, ['person', 'code'], candidate)"
          />
          <b-button
            :disabled="!nextCandidate"
            :variant="nextCandidate ? 'primary' : 'secondary'"
            @click="nextChange"
          >
            <span>{{ $lang.paginate.next }}</span>
            <mdicon name="chevron-left" size="24" />
          </b-button>
        </div>
        <b-row>
          <b-col>
            <OverviewTable
              :columns="availableExercises"
              :items="overviewTableItems"
              pivot_key="exercises"
              @cellClicked="competencyExerciseClicked"
              @overallClicked="competencyOverallClicked"
            />
          </b-col>
        </b-row>

        <b-modal
          size="lg"
          no-close-on-backdrop
          v-model="scoreModal"
          hide-footer
          header-bg-variant="primary"
        >
          <template slot="modal-header">
            <span v-html="score_title"></span>
          </template>
          <b-overlay :show="scoreLoading" rounded="sm" :opacity="0.5">
            <b-form @submit.prevent="saveScore">
              <b-alert v-if="includes(executive_role, [3, 5])" show dismissible>
                {{ $lang.changeFinalDescription }}
              </b-alert>
              <div v-if="executive_role === 4">
                {{ $lang.score_selected }}:
                {{ score_selected && score_selected.text }}
              </div>
              <div>
                {{ $lang.score_suggest }}:
                {{ pathOr("", ["score_suggest"], item_selected) }}
              </div>
              <b-form-group
                :label="$lang.score_selected"
                label-for="category_id"
                :label-cols="12"
              >
                <requiredComponent
                  slot="label"
                  :label="
                    mode === 'score'
                      ? $lang.score_selected
                      : $lang.overview_rating
                  "
                  :rules="$v.score_selected.$params"
                  v-if="mode === 'score'"
                />
                <selectFind
                  name="category_id"
                  :options="score_options"
                  v-model="$v.score_selected.$model"
                  :disabled="!includes(executive_role, [3, 5])"
                  label="text"
                  id="value"
                  :isValid="
                    $v.score_selected.$dirty ? !$v.score_selected.$error : null
                  "
                  :loading="overallRatingLoading"
                />

                <b-form-invalid-feedback id="category_id-feedback">
                  {{ $lang.error_required | replace($lang.score_selected) }}
                </b-form-invalid-feedback>
              </b-form-group>
              <b-form-group
                :label="$lang.name"
                label-for="name"
                :label-cols="this.$config.form.labelCol"
              >
                <requiredComponent
                  slot="label"
                  :label="$lang.finalOverall"
                  :rules="$v.final_description.$params"
                />
                <Editor
                  :rows="4"
                  :cols="50"
                  dir="rtl"
                  :toolbarItems="editorToolbarItems"
                  v-model="final_description"
                />
                <b-form-invalid-feedback id="name-feedback"
                  >{{ $lang.error_required | replace($lang.description) }}
                </b-form-invalid-feedback>
              </b-form-group>
              <div slot="footer" class="form-group form-btn">
                <b-button
                  v-if="executive_role"
                  data-testid="submit-button"
                  type="submit"
                  size="md"
                  variant="success"
                >
                  {{ $lang.submitButton }}
                  <i class="fa fa-check"></i>
                </b-button>

                <b-button
                  class="ml-2"
                  size="md"
                  variant="danger"
                  @click="cancelEditCompetency"
                >
                  {{ $lang.cancelButton }}
                  <i class="fa fa-ban"></i>
                </b-button>
              </div>
            </b-form>
          </b-overlay>
        </b-modal>
        <b-modal
          size="lg"
          no-close-on-backdrop
          v-model="overallModal"
          hide-footer
          header-bg-variant="primary"
        >
          <template slot="modal-header">
            <span v-html="overall_title"></span>
          </template>
          <b-overlay :show="overallLoading" rounded="sm" :opacity="0.5">
            <b-form @submit.prevent="saveOverall">
              <b-form-group
                :label="$lang.name"
                label-for="name"
                :label-cols="this.$config.form.labelCol"
              >
                <requiredComponent
                  slot="label"
                  :label="$lang.overall"
                  :rules="$v.overall.$params"
                />
                <b-form-textarea
                  data-testid="name"
                  type="text"
                  name="overall"
                  class="name"
                  :autocomplete="$lang.overall"
                  :state="$v.overall.$dirty ? !$v.overall.$error : null"
                  v-model="$v.overall.$model"
                ></b-form-textarea>
                <b-form-invalid-feedback id="name-feedback"
                  >{{ $lang.error_required | replace($lang.overall) }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group
                :label="$lang.strength_and_development"
                label-for="description"
                :label-cols="12"
              >
                <requiredComponent
                  slot="label"
                  :label="$lang.strength_and_development"
                  :rules="$v.strength_and_development.$params"
                />
                <b-form-textarea
                  name="description"
                  data-testid="description"
                  :state="
                    $v.strength_and_development.$dirty
                      ? !$v.strength_and_development.$error
                      : null
                  "
                  v-model="$v.strength_and_development.$model"
                  type="text"
                  :autocomplete="$lang.strength_and_development"
                  :rows="this.$config.form.textareaRows"
                >
                </b-form-textarea>
                <b-form-invalid-feedback id="description-feedback">
                  {{
                    $lang.error_required
                      | replace($lang.strength_and_development)
                  }}</b-form-invalid-feedback
                >
              </b-form-group>

              <b-form-group
                :label="$lang.category"
                label-for="category_id"
                :label-cols="12"
              >
                <requiredComponent
                  slot="label"
                  :label="$lang.score_selected"
                  :rules="$v.score_selected.$params"
                />

                <selectFind
                  name="category_id"
                  :options="score_options"
                  v-model="$v.score_selected.$model"
                  label="text"
                  id="value"
                  :isValid="
                    $v.score_selected.$dirty ? !$v.score_selected.$error : null
                  "
                  :loading="overallRatingLoading"
                />

                <b-form-invalid-feedback id="category_id-feedback"
                  >{{ $lang.error_required | replace($lang.category) }}
                </b-form-invalid-feedback>
              </b-form-group>

              <div slot="footer" class="form-group form-btn">
                <b-button
                  data-testid="submit-button"
                  type="submit"
                  size="md"
                  variant="success"
                >
                  {{ $lang.submitButton }}
                  <i class="fa fa-check"></i>
                </b-button>

                <b-button
                  class="ml-2"
                  size="md"
                  variant="danger"
                  @click="cancelEditExercise"
                >
                  {{ $lang.cancelButton }}
                  <i class="fa fa-ban"></i>
                </b-button>
              </div>
            </b-form>
          </b-overlay>
        </b-modal>
      </b-col>
    </b-row>
  </b-overlay>
</template>
<script>
import OverviewTable from "./overviewTable";
import Editor from "@/components/ckeditor/editor";
import AvatarInfo from "@/components/global/AvatarInfo";
import { required } from "vuelidate/lib/validators";
import { addAttachmentCode } from "@/components/ckeditor/submitAttachment";
import { pathOr, includes } from "ramda";
import { renderEumOptions } from "@/shared/utils";

export default {
  name: "overview",
  components: {
    OverviewTable,
    Editor,
    AvatarInfo,
  },
  inject: [
    "centerExecuteCandidateOverviewRepository",
    "centerExecuteCompetencyRepository",
    "centerExecuteCaseFinalTextRepository",
  ],
  props: {
    center_execute_id: {
      type: String,
      required: true,
    },
    center_execute_candidate_id: {
      type: String,
      required: true,
    },
  },
  validations: {
    calculated_score: {},
    score_selected: {
      required,
    },
    description: {},
    overall: {},
    strength_and_development: {},
    evidanceFinalText: {
      // required
    },
    final_description: {},
  },
  data() {
    return {
      // تنظیمات ckeditor
      editor: "ClassicEditor",
      editorConfig: {
        toolbar: [
          "heading",
          "bulletedList",
          "numberedList",
          "fontColor",
          "fontBackgroundColor",
          "undo",
          "redo",
          "ImageStyle",
          "underline",
          "strikethrough",
          "subscript",
          "superscript",
        ],

        language: {
          // language: fa,
          // The UI will be fa.
          ui: "fa",
          // But the content will be edited in fa.
          content: "fa",
        },
      },
      // فیلد‌های مربوط به فرم جمع بندی

      overallRatingLoading: false,
      strength_and_development: null,
      overall: null,
      score_selected: null,
      isActive: false,
      calculated_score: 0,
      score_selected_id: null,
      description: null,
      isLoading: false,
      calculateLoading: false,
      scoreModal: false,
      executive_role: null,
      overallModal: false,
      scoreLoading: false,
      overallLoading: false,
      score_title: "",
      overall_title: "",
      person: "",
      descriptionloading: false,
      // مد لازم برای تغییر فرم جمع بندی شایستگی و تمرین (آخرین تحلیل، فرم این دو مورد باهم تفاوتی ندارند)
      mode: "competency",
      // فیلد‌های مربوط به پر کردن جدول
      exercises: [],
      competencies: [],
      items: [],
      item_selected: null,
      evidanceFinalText: null,
      final_description: "",
      nextCandidate: null,
      prevCandidate: null,
      candidate: null,
      editorToolbarItems: [
        "alignment",
        "subscript",
        "superscript",
        "|",
        "blockQuote",
        "bulletedList",
        "numberedList",
        "todoList",
        "|",
        "undo",
        "redo",
      ],
    };
  },
  computed: {
    // شایستگی‌های در دسترس برای جدول
    availableCompetencies() {
      return [
        ...this.competencies.map((competency) => {
          return {
            ...competency,
            caption: pathOr(null, ["name"], competency),
            type: "score",
          };
        }),
        {
          id: "overall",
          type: "overall",
          caption: this.$lang.overall_exercise,
          bold: true,
        },
      ];
    },
    // تمرینات در دسترس برای جدول
    availableExercises() {
      return [
        ...this.exercises.map((exercise) => {
          return {
            ...exercise,
            caption: pathOr(null, ["name"], exercise),
            type: "score",
          };
        }),
        {
          id: 1000,
          type: "average",
          caption: this.$lang.recommended_score,
          bold: true,
        },
        {
          id: 1001,
          type: "overall",
          caption: this.$lang.overall_competency,
          bold: true,
        },
      ];
    },
    // داده‌های جدول جمع بندی شایستگی
    overviewTableItems() {
      const items = [];
      this.items.map((item) => {
        const competency = this.competencies.find(
          (competency) => competency.id === item.competency_id
        );
        const exercise = this.exercises.find(
          (exercise) => exercise.id === item.exercise_id
        );
        const x = items.find(
          (itemValue) => itemValue.id === item.competency_id
        );
        if (x) {
          const exerciseRow = {
            ...exercise,
            type: "score",
            score: item.score,
            description: item.description,
            case_competency_id: item.id,
            competency_id: item.competency_id,
            exercise_id: item.exercise_id,
            case_id: item.case_id,
            final_description: item.final_description,
            is_change_final_description: item.is_change_final_description,
            evidence_log_count: pathOr(0, ["evidence_log_count"], item),
            score_suggest: pathOr(0, ["score_suggest"], item),
          };
          x.exercises.push(exerciseRow);
        } else {
          const row = {
            ...competency,
            caption: pathOr("", ["name"], competency),
            type: "score",
            exercises: [
              {
                ...exercise,
                type: "score",
                score: item.score,
                description: item.description,
                case_competency_id: item.id,
                competency_id: item.competency_id,
                exercise_id: item.exercise_id,
                case_id: item.case_id,
                final_description: item.final_description,
                is_change_final_description: item.is_change_final_description,
              },
            ],
            evidence_log_count: pathOr(0, ["evidence_log_count"], item),
            score_suggest: pathOr(0, ["score_suggest"], item),
          };
          items.push(row);
        }
      });
      const overall = {
        type: "overall",
        caption: this.$lang.overall_exercise,
        exercises: [],
      };
      this.exercises.map((ex) => {
        overall.exercises.push({
          ...ex,
          score: ex.score,
        });
      });
      items.push(overall);
      return items;
    },
    // داده‌های جدول جمع بندی تمرین
    exerciseCompetencies() {
      let items = [];
      this.items.map((item) => {
        let competency = this.competencies.find(
          (x) => x.id == item.competency_id
        );
        let exercise = this.exercises.find((x) => x.id == item.exercise_id);
        let x = items.find((x) => x.id == item.exercise_id);
        if (x) {
          let e = {
            ...competency,
            type: "score",
            score: item.score,
            case_competency_id: item.id,
            competency_id: item.competency_id,
            exercise_id: item.exercise_id,
            description: item.description,
            final_description: item.final_description,
            case_id: item.case_id,
          };
          x.competencies.push(e);
        } else {
          let e = {
            ...competency,
            type: "score",
            score: item.score,
            case_competency_id: item.id,
            competency_id: item.competency_id,
            exercise_id: item.exercise_id,
            description: item.description,
            final_description: item.final_description,
            case_id: item.case_id,
          };
          let c = {
            ...exercise,
            caption: (exercise && exercise.name) || null,
            type: "score",
            competencies: [e],
          };
          items.push(c);
        }
        // item.caption = item.type = "score";
        // item.caption = item.name;
        // items.push(item);
      });

      let overall = {
        caption: this.$lang.overall_competency,
        type: "overall",
        competencies: [],
      };
      this.competencies.map((ex) => {
        overall.competencies.push({
          ...ex,
          score: ex.score,
        });
      });
      items.push(overall);
      return items;
    },
    score_options() {
      let options = renderEumOptions("competency_score");
      return options.map((item) => {
        if (!item.value) {
          item.text = item.text.replace("0", "w");
        }
        return item;
      });
    },
  },
  watch: {
    $route() {
      this.getCandidateOverview();
    },
    score_selected(newValue, oldValue) {
      const oldId = pathOr(null, ["value"], oldValue);
      const newId = pathOr(null, ["value"], newValue);
      if (!oldId || !newId) return;
      this.getFinalText();
    },
  },
  created() {
    this.getCandidateOverview();
  },
  methods: {
    pathOr,
    includes,
    async getFinalText() {
      const caseId = pathOr(null, ["case_id"], this.item_selected);
      const competencyId = pathOr(null, ["competency_id"], this.item_selected);
      if (!includes(this.executive_role, [3, 5]) || !caseId || !competencyId)
        return;

      const scoreSelected = pathOr(null, ["value"], this.score_selected);
      if (scoreSelected === null) return;

      if (scoreSelected >= 0 || scoreSelected === "w") {
        try {
          this.scoreLoading = true;
          const payload = {
            score: scoreSelected,
            description: null,
          };
          const response =
            await this.centerExecuteCaseFinalTextRepository.partial_edit(
              this.center_execute_id,
              caseId,
              competencyId,
              payload
            );
          this.final_description = pathOr(null, ["data", "data"], response);
        } catch (error) {
          this.$generateError(error);
        } finally {
          this.scoreLoading = false;
        }
      } else {
        this.$notify.error(this.$lang.error_score_competency);
      }
    },
    nextChange() {
      this.$emit("changeNextPrev", this.nextCandidate);
    },
    prevChange() {
      this.$emit("changeNextPrev", this.prevCandidate);
    },
    // پاکسازی فرم‌ها
    clear() {
      this.isLoading = false;
      this.scoreLoading = false;
      this.overallLoading = false;
      this.overallModal = false;
      this.scoreModal = false;
      this.strength_and_development = null;
      this.overall = null;
      this.description = "";
      this.score_selected = null;
      this.item_selected = null;
      this.evidanceFinalText = null;

      this.final_description = "";
      this.mode = "competency";
      this.score_title = "";
      this.overall_title = "";
    },
    // لغو فرم ویرایش تمرین
    cancelEditExercise() {
      this.clear();
    },
    // لغو فرم ویرایش شایستگی
    cancelEditCompetency() {
      this.clear();
    },
    // هنگامی که روی جمع بندی تمرین کلیک شد
    exerciseOverallClicked(item) {
      this.overall_title = this.$lang.exercise_overall_title.replace(
        "{0}",
        item.name
      );

      this.mode = "exercise";
      this.item_selected = item;
      this.overallModal = true;
      this.strength_and_development = item.strength_development_area;
      this.overall = item.overall;

      this.score_selected = this.score_options.find(
        (x) => x.value == item.score
      );
    },
    // هنگامی که روی جمع بندی شایستگی کلیک شد
    competencyOverallClicked(item) {
      this.overall_title = this.$lang.competency_overall_title.replace(
        "{0}",
        item.name
      );
      this.mode = "competency";
      this.overallModal = true;
      this.item_selected = item;
      this.strength_and_development = item.strength_development_area;
      this.overall = item.overall;
      this.score_selected = this.score_options.find(
        (x) => x.value === item.score
      );
    },
    // هنگامی که روی امتیاز شایستگی در جدول تمرین کلیک شد
    exerciseCompetencyClicked(item) {
      let competency = this.competencies.find(
        (x) => x.id == item.competency_id
      );
      let exercise = this.exercises.find((x) => x.id == item.exercise_id);
      this.score_title = this.$lang.competency_score_score_title
        .replace("{0}", competency.name)
        .replace("{1}", exercise.name);
      this.scoreModal = true;
      this.description = item.description;
      this.final_description = pathOr("", ["final_description"], item);
      this.score_selected = this.score_options.find(
        (x) => x.value == item.score
      );
      this.item_selected = item;
    },
    // هنگامی که روی امتیاز شایستگی در جدول شایستگی کلیک شد
    competencyExerciseClicked(item) {
      let competency = this.competencies.find(
        (x) => x.id == item.competency_id
      );
      let exercise = this.exercises.find((x) => x.id == item.exercise_id);
      this.score_title = this.$lang.competency_score_score_title
        .replace("{0}", competency.name)
        .replace("{1}", exercise.name);
      this.scoreModal = true;
      this.description = item.description;
      this.final_description = item.final_description
        ? item.final_description
        : "";
      this.score_selected = this.score_options.find(
        (x) => x.value == item.score
      );
      this.item_selected = item;
    },
    onReady(editor) {
      editor.ui
        .getEditableElement()
        .parentElement.insertBefore(
          editor.ui.view.toolbar.element,
          editor.ui.getEditableElement()
        );
    },
    // دریافت داده‌های جمع بندی شرکت کننده
    async getCandidateOverview() {
      try {
        this.isLoading = true;
        const params = {
          order_by: "model_type:asc-model_id:asc",
        };
        const response =
          await this.centerExecuteCandidateOverviewRepository.list(
            this.$route.params.center_execute_id,
            this.$route.params.center_execute_candidate_id,
            params
          );
        const data = pathOr(null, ["data", "data"], response);
        this.items = pathOr([], ["items"], data);
        this.executive_role = pathOr(null, ["executive_role", "id"], data);
        this.competencies = pathOr([], ["competency"], data);
        this.exercises = pathOr([], ["exercise"], data);
        this.nextCandidate = pathOr(null, ["next"], data);
        this.prevCandidate = pathOr(null, ["prev"], data);
        this.person = pathOr(
          null,
          ["candidate", "person_center_execute_code"],
          data
        );
        this.candidate = pathOr(null, ["candidate"], data);
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.isLoading = false;
      }
    },
    // ذخیره امتیاز
    async saveScore(allowAddAttachmentCode = true) {
      this.scoreLoading = true;
      this.$v.$touch();
      if (this.$v.$anyError) {
        this.scoreLoading = false;
        return;
      }
      let payload = {
        score: this.score_selected.value,
        final_description: this.final_description,
        description: this.description,
        location: "overview",
      };
      try {
        let res = await this.centerExecuteCompetencyRepository.edit(
          this.center_execute_id,
          this.item_selected.case_id,
          this.item_selected.competency_id,
          payload
        );

        let id = this.item_selected.case_competency_id;
        if (allowAddAttachmentCode) {
          let result = await addAttachmentCode(
            "center_execute_case_competency",
            id,
            this.final_description
          );
          if (result.fail_codes) {
            this.final_description = result.description;
            await this.saveScore(false);
          } else {
            this.$notify.success(res.data.message);
            this.$emit("competencyUpdated");
            this.clear();
            this.getCandidateOverview();
          }
        } else {
          this.$notify.success(res.data.message);
          this.$emit("competencyUpdated");
          this.clear();
          this.getCandidateOverview();
        }
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.scoreLoading = false;
      }
    },
    // ذخیره جمع بندی
    async saveOverall() {
      this.overallLoading = true;
      this.$v.$touch();
      if (this.$v.$anyError) {
        this.overallLoading = false;
        return;
      }

      let payload = {
        score: this.score_selected.value,
        strength_development_area: this.strength_and_development
          ? this.strength_and_development
          : null,
        overall: this.overall,
      };
      try {
        let res = await this.centerExecuteCandidateOverviewRepository.edit(
          this.center_execute_id,
          this.$route.params.center_execute_candidate_id,
          this.item_selected.overview_id,
          payload
        );
        this.$notify.success(res.data.message);

        this.$emit("competencyUpdated");
        this.clear();
        this.getCandidateOverview();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.overallLoading = false;
      }
    },
  },
};
</script>
<style lang="css">
.ck-editor__editable {
  min-height: 300px;
}

.competency_item > .dropdown-menu {
  width: 100%;
}

.white-space-auto {
  display: flex;
  white-space: nowrap;
  overflow: auto;
}

.btn-active {
  background-color: rgb(16, 115, 150);
  color: white;
  border: 1px solid;
}

.card-description {
  border: 1px solid lightgray;
  min-height: 100px;
}

.modalmain {
  /* Hidden by default */
  position: fixed;
  /* Stay in place */
  z-index: 1;
  /* Sit on top */
  left: 0;
  top: 0;
  width: 100%;
  /* Full width */
  height: 100%;
  /* Full height */
  overflow: auto;
  /* Enable scroll if needed */
  background-color: rgb(0, 0, 0);
  /* Fallback color */
  background-color: rgba(0, 0, 0, 0.4);
  /* Black w/ opacity */
}

/* Modalmain Content/Box */
.modalmain-content {
  background-color: #fefefe;
  margin: 15% auto;
  /* 15% from the top and centered */
  padding: 20px;
  border: 1px solid #888;
  width: 80%;
  /* Could be more or less, depending on screen size */
}

/* The Close Button */
.close {
  color: #aaa;
  float: right;
  font-size: 28px;
  font-weight: bold;
}

.close:hover,
.close:focus {
  color: black;
  text-decoration: none;
  cursor: pointer;
}
</style>
